import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Package, ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BackButton from '../components/ui/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    loadProduct();
    loadSettings();
  }, [id]);

  const loadProduct = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/products/${id}`);
      if (!res.ok) { navigate('/shop'); return; }
      const data = await res.json();
      setProduct(data);
    } catch {
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/settings`);
      const data = await res.json();
      setSettings(data);
    } catch {}
  };

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem('shop_cart') || '[]');
    const existing = cart.find((i) => i.product_id === product.id);
    if (existing) {
      existing.quantity += quantity;
    } else {
      cart.push({ product_id: product.id, quantity, name: product.name, price: product.price, image: product.images?.[0] });
    }
    localStorage.setItem('shop_cart', JSON.stringify(cart));
    toast({ title: 'Added to cart!', description: `${product.name} × ${quantity}` });
  };

  const sym = settings.currency_symbol || 'CA$';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!product) return null;

  const images = product.images?.length ? product.images : [];
  const inStock = product.stock === undefined || product.stock > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <BackButton />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="md:flex">
            {/* Image Gallery */}
            <div className="md:w-1/2">
              <div className="relative aspect-square bg-gray-100">
                {images.length > 0 ? (
                  <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-24 h-24 text-gray-300" />
                  </div>
                )}
                {images.length > 1 && (
                  <>
                    <button onClick={() => setActiveImg((p) => (p - 1 + images.length) % images.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1 shadow">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => setActiveImg((p) => (p + 1) % images.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1 shadow">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setActiveImg(i)}
                      className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${activeImg === i ? 'border-pink-500' : 'border-transparent'}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="md:w-1/2 p-6 flex flex-col justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
                <p className="text-3xl font-bold text-pink-600 mb-4">
                  {sym}{parseFloat(product.price).toFixed(2)}
                </p>
                {product.description && (
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{product.description}</p>
                )}
                <div className={`inline-flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full mb-6 ${inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {inStock ? 'In Stock' : 'Out of Stock'}
                </div>
              </div>

              {inStock && (
                <div>
                  {/* Quantity */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm font-medium text-gray-700">Quantity:</span>
                    <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                      <button onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="p-2 hover:bg-gray-100 transition-colors" data-testid="qty-minus">
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-4 text-sm font-semibold" data-testid="qty-value">{quantity}</span>
                      <button onClick={() => setQuantity((q) => q + 1)}
                        className="p-2 hover:bg-gray-100 transition-colors" data-testid="qty-plus">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={addToCart}
                    className="w-full bg-pink-500 hover:bg-pink-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Add to Cart
                  </button>
                  <button
                    onClick={() => { addToCart(); navigate('/shop/cart'); }}
                    className="w-full mt-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                    data-testid="buy-now-btn"
                  >
                    Buy Now
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopProductDetailPage;
